/**
 * Opening a link that belongs to the web rather than to this app.
 *
 * `window.open` is the obvious way and the wrong one. Inside an installed app
 * it opens a window the *app* owns: no tabs, no address bar, no history, a pane
 * that can only show that one page. It is the right shape for a payment popup
 * and the wrong shape for "I clicked a link in my notes", where what you almost
 * always want next is to keep it, search from it, or share it.
 *
 * Activating a real anchor instead routes the navigation through the browser's
 * ordinary external-link handling, which opens a proper tab. The element has to
 * exist in the document and be clicked as a genuine user activation for that to
 * hold — a synthetic `MouseEvent` on a detached node is treated as script, and
 * script gets the popup treatment again.
 *
 * What this cannot do is choose *which* browser. No web page can: handing a URL
 * to another application is the operating system's decision, and an installed
 * app is a client of the browser it was installed from. A link opened from an
 * app installed under Chrome opens in Chrome, whatever the system default is.
 */
export function openExternal(href: string): void {
  if (!href) return;

  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.target = '_blank';
  // `noopener` is what stops the opened page reaching back through
  // `window.opener`; `noreferrer` keeps the note's URL out of its logs.
  anchor.rel = 'noopener noreferrer';
  anchor.style.position = 'fixed';
  anchor.style.opacity = '0';
  anchor.style.pointerEvents = 'none';

  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
  }
}

/** True for a link that leaves the app, as opposed to one of its own routes. */
export function isExternalHref(href: string): boolean {
  if (!href) return false;
  // Protocol-relative and absolute URLs on another origin both leave; anything
  // starting with a single slash is one of this app's own screens.
  if (href.startsWith('//')) return true;
  if (href.startsWith('/')) return false;
  if (/^[a-z][a-z0-9+.-]*:/i.test(href)) {
    try {
      return new URL(href, location.href).origin !== location.origin;
    } catch {
      // A scheme we cannot parse — `mailto:`, `tel:` — is still not this app.
      return true;
    }
  }
  return false;
}
